import { useCallback, useRef, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/components/common';
import { installPromptStore } from '@/lib/pwa-install';

export function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // iOS Safari answers no display-mode query; it carries its own flag.
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

/**
 * The install offer targets phones and tablets, where the app lives on a home
 * screen. Matching on device capability rather than the user agent keeps it
 * honest: a coarse pointer on a tablet-or-smaller viewport is a touch device.
 * The cap must admit tablets in landscape too (an iPad Pro 12.9" is 1366 CSS px
 * wide), and the query is subscribed to, so rotating re-evaluates it.
 */
const HANDHELD_QUERY = '(pointer: coarse) and (max-width: 1366px)';

function subscribeHandheld(onChange: () => void): () => void {
  const mediaQuery = window.matchMedia(HANDHELD_QUERY);
  mediaQuery.addEventListener('change', onChange);
  return () => mediaQuery.removeEventListener('change', onChange);
}

function isHandheld(): boolean {
  return window.matchMedia(HANDHELD_QUERY).matches;
}

function isIos(): boolean {
  const ua = navigator.userAgent;
  // An iPad on iPadOS 13+ claims to be a Mac, and only the touch points give it away.
  return /iphone|ipad|ipod/i.test(ua) || (/macintosh/i.test(ua) && navigator.maxTouchPoints > 1);
}

/**
 * Drives the install offer. Chrome hands over a `beforeinstallprompt` event
 * that is captured once per page load at module scope (`lib/pwa-install`) and
 * replayed from here, so a surface mounted after the event still installs; iOS
 * fires nothing at all, so there the only route is telling the user which
 * Safari menu to use.
 */
export function useInstallPrompt() {
  const { t } = useTranslation('common');
  const { toast } = useToast();
  const deferred = useSyncExternalStore(
    installPromptStore.subscribe,
    installPromptStore.getDeferred
  );
  const installedThisLoad = useSyncExternalStore(
    installPromptStore.subscribe,
    installPromptStore.wasInstalledThisLoad
  );
  const handheld = useSyncExternalStore(subscribeHandheld, isHandheld);
  // Deliberately not persisted: the X means "not right now", so the offer
  // returns on the next visit. Only this page load remembers the no.
  const [isDismissed, setIsDismissed] = useState(false);
  const installingRef = useRef(false);

  const isInstalled = installedThisLoad || isStandalone();

  const dismiss = useCallback(() => {
    setIsDismissed(true);
  }, []);

  const install = useCallback(async () => {
    const event = installPromptStore.getDeferred();
    if (!event || installingRef.current) return;
    installingRef.current = true;

    try {
      await event.prompt();
      // Backing out of Chrome's own sheet is hesitation, not the banner's final
      // "no": only the explicit dismiss button writes the stored decision, and
      // Chrome fires a fresh event on a later visit anyway.
      await event.userChoice;
    } catch {
      toast({ title: t('install.failed'), variant: 'error' });
    } finally {
      // The event is single use, so it is dropped either way.
      installPromptStore.clearDeferred();
      installingRef.current = false;
    }
  }, [toast, t]);

  const needsIosSteps = !isInstalled && isIos();
  const isOfferable = !!deferred || needsIosSteps;

  return {
    /** Chrome can install on the spot; iOS only gets the manual steps. */
    canPrompt: !!deferred,
    needsIosSteps,
    isInstalled,
    /**
     * The unsolicited offer is for handhelds only, where the app belongs on a
     * home screen; dismissing hides it for this page load and it comes back
     * on the next visit. The settings entry point ignores the dismissal.
     */
    isOfferOpen: isOfferable && !isInstalled && !isDismissed && handheld,
    isOfferable,
    install,
    dismiss,
  };
}
